import type { StatusCategory } from '@servicetim/shared';
import { Badge, type BadgeProps } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { initials } from './format';

const CATEGORY_VARIANT: Record<StatusCategory, BadgeProps['variant']> = {
  todo: 'secondary',
  in_progress: 'info',
  done: 'success',
};

export const CATEGORY_LABELS: Record<StatusCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

export interface StatusBadgeProps {
  /** Label from the `display` map (or the status name). */
  label: string | null | undefined;
  category?: StatusCategory | null;
  color?: string | null;
  className?: string;
}

/** Status pill coloured by workflow category; the status colour becomes a dot. */
export function StatusBadge({ label, category, color, className }: StatusBadgeProps) {
  return (
    <Badge
      variant={category ? CATEGORY_VARIANT[category] : 'outline'}
      dot={color ?? null}
      className={className}
      data-category={category ?? undefined}
    >
      {label ?? 'Unknown status'}
    </Badge>
  );
}

export function PriorityBadge({
  label,
  color,
  className,
}: {
  label: string | null | undefined;
  color?: string | null;
  className?: string;
}) {
  if (!label) return <span className="text-fg-muted text-xs">No priority</span>;
  return (
    <Badge variant="outline" dot={color ?? null} className={className}>
      {label}
    </Badge>
  );
}

/** Initials avatar; the full name is in the tooltip and for screen readers. */
export function Avatar({
  name,
  className,
  size = 'sm',
}: {
  name: string | null | undefined;
  className?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      role="img"
      title={name ?? undefined}
      aria-label={name ?? 'Unassigned'}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium',
        name
          ? 'bg-brand-100 text-brand-800'
          : 'border border-border-strong border-dashed text-fg-muted',
        size === 'sm' ? 'size-6 text-[10px]' : 'size-8 text-xs',
        className,
      )}
    >
      {name ? initials(name) : '–'}
    </span>
  );
}

/** Avatar + name; "Unassigned" when there is no one. */
export function UserChip({
  name,
  emptyLabel = 'Unassigned',
  className,
}: {
  name: string | null | undefined;
  emptyLabel?: string;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-sm', className)}>
      <Avatar name={name} />
      <span className={cn('truncate', !name && 'text-fg-muted')}>{name ?? emptyLabel}</span>
    </span>
  );
}
